import { useEffect, useState } from "react";
import type { Socket } from "socket.io-client";
import { getMembers } from "@/services/chat";
import { getServiceUserList } from "@/services/service-users";
import {
  listFamilyChatMembers,
  normalizeFamilyChatMembers,
} from "@/services/service-users/family-access";
import { loadFamilyChatSidebar } from "@/utils/chat/family-chat-sidebar";

// Counts belong to the signed-in shell; opening Messages is not a prerequisite.
export function useMessageUnreadCounts({
  userId,
  tenant,
  socket,
}: {
  userId?: string;
  tenant?: string;
  socket: Socket | null;
}) {
  const [counts, setCounts] = useState({
    owner: "",
    staff: [] as string[],
    family: 0,
  });
  const owner = JSON.stringify([tenant, userId]);
  useEffect(() => {
    if (!userId || !tenant) return;
    let active = true;
    const loadStaff = async () => {
      const unread: string[] = [];
      for (let page = 1; active; page++) {
        const response = await getMembers(userId, { page, limit: 100 });
        if (!response?.success || !Array.isArray(response.data))
          throw new Error("Unread messages unavailable");
        for (const [sender, count] of Object.entries(
          response.unreadMsgs || {}
        )) {
          if (
            typeof count === "number" &&
            Number.isSafeInteger(count) &&
            count > 0
          )
            unread.push(...Array(count).fill(sender));
        }
        if (response.data.length < 100) return unread;
      }
      return unread;
    };
    const loadFamily = () =>
      loadFamilyChatSidebar({
        getServiceUsers: getServiceUserList,
        listMembers: listFamilyChatMembers,
        normalizeMembers: normalizeFamilyChatMembers,
        isCurrent: () => active,
      }).then((groups) =>
        groups.reduce((total, group) => total + group.totalUnread, 0)
      );
    // Each source owns its own coalescing loop. Staff activity must not scan the
    // family catalogue, or wait for an unrelated family request to complete.
    const refresher = <T>(
      load: () => Promise<T>,
      apply: (value: T) => void
    ) => {
      let running = false;
      let dirty = false;
      return async () => {
        dirty = true;
        if (running) return;
        running = true;
        try {
          while (active && dirty) {
            dirty = false;
            try {
              const value = await load();
              if (active && !dirty) apply(value);
            } catch {
              // Keep the current owner's last confirmed count on failure.
            }
          }
        } finally {
          running = false;
        }
      };
    };
    const refreshStaff = refresher(loadStaff, (staff) =>
      setCounts((current) => ({
        owner,
        staff,
        family: current.owner === owner ? current.family : 0,
      }))
    );
    const refreshFamily = refresher(loadFamily, (family) =>
      setCounts((current) => ({
        owner,
        family,
        staff: current.owner === owner ? current.staff : [],
      }))
    );
    const updateStaff = () => {
      void refreshStaff();
    };
    const updateFamily = () => {
      void refreshFamily();
    };
    const updateBoth = () => {
      updateStaff();
      updateFamily();
    };
    const onRead = (event: Event) => {
      const scope = (event as CustomEvent<{ scope?: string }>).detail?.scope;
      if (scope === "staff") updateStaff();
      else if (scope === "family") updateFamily();
      else updateBoth(); // Preserve compatibility with unscoped read emitters.
    };
    updateBoth();
    socket?.on("connect", updateBoth);
    socket?.on("global-message", updateStaff);
    socket?.on("family-chat-updated", updateFamily);
    window.addEventListener("focus", updateBoth);
    window.addEventListener("wisterias:messages-read", onRead);
    return () => {
      active = false;
      socket?.off("connect", updateBoth);
      socket?.off("global-message", updateStaff);
      socket?.off("family-chat-updated", updateFamily);
      window.removeEventListener("focus", updateBoth);
      window.removeEventListener("wisterias:messages-read", onRead);
    };
  }, [owner, userId, tenant, socket]);
  const current = counts.owner === owner;
  return {
    isUnreadMsgs: current ? counts.staff : [],
    familyUnreadCount: current ? counts.family : 0,
  };
}
